import React from 'react';
import { Text, StatusChip, Button } from '@fragments';
import Parser from '@helpers/parser';
import {
    IconInfoCircle,
    IconAlertCircle,
    IconUser,
} from '@tabler/icons-react';

import ContestationAlert from './components/ContestationAlert';
import EventTimeline from './components/EventTimeline';
import DetailsSidebar from './components/DetailsSidebar';
import ChargebackLogs from './components/ChargebackLogs';
import { CHARGEBACK_STATUS_MAP } from '../../tabs/Chargebacks/enums/chargebackStatuses';

import './styles.scss';

function ChargebackStatusChip({ status }) {
    const config = CHARGEBACK_STATUS_MAP[status];
    if (!config) return <StatusChip.Default text={status} />;

    const variants = {
        success: StatusChip.Success,
        error: StatusChip.Error,
        alert: StatusChip.Alert,
        info: StatusChip.Info,
        default: StatusChip.Default,
    };

    const Component = variants[config.chipVariant] || StatusChip.Default;
    return <Component text={config.label} />;
}

function getDaysUntilDeadline(deadline) {
    if (!deadline) return null;
    const diff = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
}

export default function ChargebackDetailsView({
    chargeback,
    loading,
    error,
    navigate,
    onDispute,
}) {
    if (loading) {
        return (
            <div className="px-3 pt-2 pb-9">
                <div className="chargeback-details chargeback-details--loading" />
            </div>
        );
    }

    if (error || !chargeback) {
        return (
            <div className="px-3 pt-2 pb-9">
                <div className="chargeback-details chargeback-details--error">
                    <div className="chargeback-details__error-state">
                        <IconAlertCircle size={40} style={{ opacity: 0.4 }} />
                        <Text.h4 contrast={5}>{error || 'Contestação não encontrada'}</Text.h4>
                        <Button
                            text="Voltar para chargebacks"
                            color="white"
                            onClick={() => navigate('/banking/chargebacks')}
                        />
                    </div>
                </div>
            </div>
        );
    }

    const daysUntilDeadline = getDaysUntilDeadline(chargeback.timestamps?.deadline);
    const showDeadlineBadge = daysUntilDeadline !== null && daysUntilDeadline > 0 && (chargeback.status === 'requested' || chargeback.status === 'waiting_documents');
    const deadlineIsUrgent = daysUntilDeadline !== null && daysUntilDeadline <= 5 && daysUntilDeadline > 0;

    return (
        <div className="px-3 pt-2 pb-9">
            <div className="chargeback-details">
                {/* Page header */}
                <div className="chargeback-details__header pt-6">
                    <div className="chargeback-details__header-left  mt-3">
                        <div className="chargeback-details__amount-row">
                            <Text.h2 weight={400}>
                                {Parser.price.format(chargeback.amount, chargeback.currency)} {chargeback.currency || 'BRL'}
                            </Text.h2>
                            {showDeadlineBadge && (
                                <span className={`chargeback-details__deadline-badge${deadlineIsUrgent ? ' chargeback-details__deadline-badge--urgent' : ''}`}>
                                    <IconInfoCircle size={13} />
                                    {daysUntilDeadline} {daysUntilDeadline === 1 ? 'dia' : 'dias'} restantes para envio
                                </span>
                            )}
                            <ChargebackStatusChip status={chargeback.status} />
                        </div>

                        {chargeback.user && (
                            <Text.span contrast={6} weight={400} left={<IconUser />}>
                                Cliente:{' '}
                                <Text.span
                                    weight={400}
                                    contrast={6}
                                    className="chargeback-details__user-link"
                                    onClick={() => navigate(`/user/${chargeback.user.id}`)}
                                    role="button"
                                >
                                    {chargeback.user.username}
                                </Text.span>
                            </Text.span>
                        )}
                    </div>
                </div>

                {/* Two-column layout */}
                <div className="chargeback-details__layout">
                    <div className="chargeback-details__main">
                        <ContestationAlert
                            reason={chargeback.reason}
                            status={chargeback.status}
                            onAccept={() => { }}
                            onDispute={onDispute}
                        />
                        <div className="px-1 mt-2">
                            <EventTimeline events={chargeback.events || []} />
                        </div>

                        {chargeback.user && chargeback.plan?.community && (
                            <ChargebackLogs
                                communityId={chargeback.plan.community}
                                userId={chargeback.user.id}
                                username={chargeback.user.username}
                            />
                        )}
                    </div>

                    <aside className="chargeback-details__sidebar">
                        <DetailsSidebar chargeback={chargeback} />
                    </aside>
                </div>
            </div>
        </div>
    );
}
